import { useEffect, useRef } from 'react';

/**
 * Accessible modal dialog. Closes on Escape and on backdrop click.
 * Locks body scroll while open. Focus is trapped to the dialog.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
}) {
  const dialogRef = useRef(null);
  const previousFocus = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previousFocus.current = document.activeElement;
    document.body.style.overflow = 'hidden';

    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);

    // Focus the first interactive element after mount.
    setTimeout(() => {
      const target = dialogRef.current?.querySelector(
        'input, select, textarea, button:not([data-skip-autofocus])',
      );
      target?.focus();
    }, 10);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previousFocus.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 py-6 sm:py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Round 332 (2026-06-22) — bounded height + scrollable body.
          Outer wrapper has py-6 sm:py-10, so the dialog can grow up to
          (100vh - 5rem) on desktop / (100vh - 3rem) on small screens.
          Header + footer are flex-none so Cancel/Save stay pinned at
          the bottom and the title stays at the top no matter how tall
          the body content grows; the body itself flex-1 with
          overflow-y-auto handles the rest. Generic — fixes every
          modal in the app at once (channel-form-modal, person-form-modal,
          unit/move/delete, etc). */}
      <div
        ref={dialogRef}
        className={`relative flex w-full max-h-[calc(100vh-3rem)] sm:max-h-[calc(100vh-5rem)] flex-col ${widths[size] || widths.md} rounded-3xl border border-line bg-white shadow-lg`}
      >
        <header className="flex flex-none items-start justify-between gap-4 border-b border-line px-6 py-5">
          <div>
            <h2 id="modal-title" className="text-lg font-semibold tracking-tight text-ink">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-sm text-mist">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-skip-autofocus
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-full text-mist transition-colors hover:bg-canvas hover:text-ink focus:outline-none focus-visible:shadow-focus"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
        {footer && (
          <footer className="flex flex-none items-center justify-end gap-2 border-t border-line bg-canvas px-6 py-4 rounded-b-3xl">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
